import { Component, ElementRef, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SharedModule } from '../../shared.module';
import { TranslateModule } from '@ngx-translate/core';
import { Platform } from '@ionic/angular';
import { IAvSettingSelectOption, IAvSettingsItemConfig } from '../../interfaces/av-producer/event-av-producer.interface';
import { Subscription } from 'rxjs';
import { AvProdSettingsType } from '../../const/av-producer.const';
import { AV_PROD_OPTIONS_COLOR } from '../../const/av-producer-options';
import { PasswordClass } from '../../classes/users/password.class';
import { IonButton, IonButtons, IonContent, IonDatetime, IonIcon, IonInput, IonPopover, IonRadio, IonRadioGroup, IonRange, IonSelect, IonSelectOption, IonText, IonTextarea, IonToggle, IonItem, IonNote } from '@ionic/angular/standalone';

@Component({
  selector: 'mob-settings-item-shared',
  standalone: true,
  imports: [IonItem, CommonModule, FormsModule, IonIcon, IonSelect, IonSelectOption, IonPopover, IonContent, IonRange, IonTextarea,
            IonToggle, IonText, IonRadio, IonRadioGroup, IonButtons, IonButton, IonInput, IonDatetime, IonNote,
            ReactiveFormsModule, SharedModule, TranslateModule],
  templateUrl: './mob-settings-item-shared.component.html',
  styleUrl: './mob-settings-item-shared.component.scss',
})
export class MobSettingsItemSharedComponent extends PasswordClass implements OnInit, OnDestroy {

  @ViewChild('popoverDate', {read: ElementRef}) datePicker: ElementRef | undefined;
  @ViewChild('popoverTime', {read: ElementRef}) timePicker: ElementRef | undefined;

  @Input() formGroup: FormGroup = new FormGroup([]);
  @Input() onlyInput: boolean = false;
  @Input() backgroundColor: string = 'var(--ion-color-light)';
  @Input() borderRadius: string = '0.5rem';
  @Input() isDisabled: boolean = false;

  @Output() settingChange: EventEmitter<IAvSettingsItemConfig> =
    new EventEmitter<IAvSettingsItemConfig>();
  @Output() settingFocus: EventEmitter<IAvSettingsItemConfig> =
    new EventEmitter<IAvSettingsItemConfig>();

  protected formControl: AbstractControl | undefined = undefined;
  protected colorText: string = '';
  protected colorCustom: string = '#000000';

  protected colorValueSubscription: Subscription | undefined = undefined;

  protected valueOption: IAvSettingSelectOption = {
    label: '',
    labelTranslate: '',
    valueNumber: 0,
    valueStr: '',
  };

  protected configInt: IAvSettingsItemConfig = {
    id: '',
    type: AvProdSettingsType.none,
    title: '',
    name: '',
    min: 0,
    max: 0,
    step: 1,
    options: [],
    placeholder: '',
    value: '',
  };

  constructor(@Inject(DOCUMENT) private dom: Document,
              protected platform: Platform ) {
    super(1);
  }

  @Input() set config(configIn: IAvSettingsItemConfig) {
    this.configInt = configIn;
    if (this.configInt.options.length === 0 &&
        this.configInt.type === AvProdSettingsType.selectComboNumber &&
        this.configInt.step !== 0) {
      for (let i: number = 0; i <= this.configInt.max; i++) {
        const VALUE: number = this.configInt.min + this.configInt.step * i;
        const OPTION: IAvSettingSelectOption = {
          label: VALUE.toString(),
          labelTranslate: VALUE.toString(),
          valueNumber: VALUE,
          valueStr: VALUE.toString(),
        };
        this.configInt.options.push(OPTION);
      }
    }

    if (this.colorValueSubscription !== undefined)
      this.colorValueSubscription.unsubscribe();
    if (this.configInt.type === AvProdSettingsType.color) {
      this.configInt.options = AV_PROD_OPTIONS_COLOR;
      this.colorValueSubscription = this.formControl?.valueChanges.subscribe(
        (event) => this.onColorSetValue(event)
      );
    }
  }

  ngOnInit(): void {
    this.formControl = new FormControl({value: this.configInt.value, disabled: this.isDisabled})
    this.formGroup.addControl(this.configInt.id, this.formControl);
    if (this.colorValueSubscription !== undefined)
      this.colorValueSubscription.unsubscribe();
    if (this.configInt.type === AvProdSettingsType.color) {
      this.onColorSetValue(this.formControl.value);
      this.colorValueSubscription = this.formControl.valueChanges.subscribe(
        (event) => this.onColorSetValue(event)
      );
    }
  }

  ngOnDestroy(): void {
    if (this.colorValueSubscription !== undefined)
      this.colorValueSubscription.unsubscribe();
  }

  /**
   * Function called when the user changes the value
   */
  protected onControlChange(): void {
    this.settingChange.emit(this.configInt);
  }

  /**
   * Function called when the control gets focus
   */
  protected onControlFocus() {
    this.settingFocus.emit(this.configInt);
  }

  protected onColorSetValue(event: any): void {
    if (typeof event === 'string' && event.includes('#')) {
      this.colorText = 'custom';
      this.colorCustom = event;
    } else {
      this.colorText = event;
      this.colorCustom = this.colorText2Code(event);
    }
  }

  protected onDateChange(): void {
    console.log('[SettingsItemComponent] onDatechange: ' + this.datePicker?.nativeElement.value);
    let dateStr: string | undefined = this.datePicker?.nativeElement.value.split('T')[0];
    if (dateStr !== undefined){
      this.formGroup.get(this.configInt.id)?.setValue(dateStr);
      this.formGroup.get(this.configInt.id)?.markAsDirty();
      this.onControlChange();
    }
  }

  protected onTimeChange(): void {
    console.log('[SettingsItemComponent] onTimeChange: ' + this.timePicker?.nativeElement.value);
    let timeStr: string | undefined = this.timePicker?.nativeElement.value.split('T')[1];
    if (timeStr === undefined){
      timeStr = this.timePicker?.nativeElement.value;
    }
    if (timeStr !== undefined){
      this.formGroup.get(this.configInt.id)?.setValue(timeStr);
      this.formGroup.get(this.configInt.id)?.markAsDirty();
      this.onControlChange();
    }
  }

  protected onDateTimeShow(): void {
    if ((this.configInt.type === AvProdSettingsType.time) &&
        (this.timePicker !== undefined )) {
      this.timePicker.nativeElement.value = this.formGroup.get(this.configInt.id)?.value;
    }
    else if ((this.configInt.type === AvProdSettingsType.date) &&
        (this.datePicker !== undefined )) {
      this.datePicker.nativeElement.value = this.formGroup.get(this.configInt.id)?.value;
    }
  }

  protected onColorChange(): void {
    if (this.colorText === 'custom') {
      this.colorText = 'custom';
      this.formControl?.setValue(this.colorCustom);
    } else {
      this.formControl?.setValue(this.colorText);
    }
    this.formControl?.markAsDirty();
    this.onControlChange();
  }

  protected onSliderIncrease(): void {
    if (this.configInt.type === 'numberSlider' && this.configInt.step !== 0) {
      if (this.formControl?.value + this.configInt.step > this.configInt.max) {
        this.formControl?.setValue(this.configInt.max);
      } else {
        // Avoid more than 4 decimal digits
        let newValue: number = this.formControl?.value + this.configInt.step;
        newValue = Math.round(newValue * 10000) / 10000;
        this.formControl?.setValue(newValue);
      }
      this.formControl?.markAsDirty();
      this.onControlChange();
    }
  }

  protected onSliderDecrease(): void {
    if (this.configInt.type === 'numberSlider' && this.configInt.step !== 0) {
      if (this.formControl?.value - this.configInt.step < this.configInt.min) {
        this.formControl?.setValue(this.configInt.min);
      } else {
        // Avoid more than 4 decimal digits
        let newValue: number = this.formControl?.value - this.configInt.step;
        newValue = Math.round(newValue * 10000) / 10000;
        this.formControl?.setValue(newValue);
      }
      this.formControl?.markAsDirty();
      this.onControlChange();
    }
  }

  protected onColorChangeCustom(): void {
    console.log('[SettingsItemComponent] onColorChangeCustom');
    this.colorText = 'custom';
    this.formControl?.setValue(this.colorCustom);
    this.formControl?.markAsDirty();
    this.onControlChange();
  }

  private colorText2Code(color: string): string {
    let ret: string = '#ffffff';
    switch (color) {
      case 'white':
        ret = '#ffffff';
        break;
      case 'black':
        ret = '#000000';
        break;
      case 'red':
        ret = '#ff0000';
        break;
      case 'blue':
        ret = '#1d71b8';
        break;
      case 'darkblue':
        ret = '#172039';
        break;
      case 'lightblue':
        ret = '#009fe3';
        break;
      case 'yellow':
        ret = '#ffdc00';
        break;
      case 'green':
        ret = '#00dc1e';
        break;
      case 'orange':
        ret = '#f3920b';
        break;
      case 'gray':
        ret = '#c0c0c0';
        break;
      case 'darkgray':
        ret = '#646464';
        break;
    }
    return ret;
  }

  protected onInputScroll(elementId: string): void {
    let el: HTMLElement | null = this.dom.getElementById(elementId);
    el?.scrollIntoView({ behavior: 'smooth' });
  }

  protected onTooltipClick(event: any): void {
    event.stopPropagation();
  }
}
